class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* VoltBy: the drawer's "empty" look is a class on <cart-drawer>, set by Liquid
     when the page loads. Several add-to-cart buttons on the store (the buy box,
     the comparison cards, the generator) re-render the drawer's contents but
     never took that class off, so the first item added to an empty cart
     rendered into a drawer that was still styled as empty: header, items and
     footer all hidden - a blank white panel with no close button, and the page
     behind it scroll-locked. The class now always follows what is actually in
     the drawer. */
  syncEmptyState() {
    const hasItems = !!this.querySelector('#CartDrawer .cart-item');
    this.classList.toggle('is-empty', !hasItems);
    const inner = this.querySelector('.drawer__inner');
    if (inner && hasItems) inner.classList.remove('is-empty');
  }

  /* VoltBy: when the drawer is opened by something that changed the cart
     without handing over fresh drawer markup, it would show whatever it held
     when the page loaded. Opening now re-reads the cart once, so it always
     shows what is really in it. */
  refreshContents() {
    if (this.refreshing) return this.refreshing;
    const url = (window.location.pathname || '/') + '?sections=cart-drawer';
    this.refreshing = fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const html = json && json['cart-drawer'];
        const target = document.getElementById('CartDrawer');
        if (!html || !target) return;
        const fresh = this.getSectionDOM(html, '#CartDrawer');
        if (!fresh) return;
        target.innerHTML = fresh.innerHTML;
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.syncEmptyState();
      })
      .catch(() => {})
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.syncEmptyState();
    if (!this.justRendered) this.refreshContents();
    this.justRendered = false;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState && parsedState.id;
    const sections = parsedState && parsedState.sections;
    let rendered = false;
    if (sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !sections[section.id]) return;
        try {
          sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
          if (section.id === 'cart-drawer') rendered = true;
        } catch (e) {}
      });
    }
    this.syncEmptyState();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.justRendered = rendered;
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
